/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { ITooltipHostProps, TooltipHost } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type LuisTooltipProps = ITooltipHostProps & {
    /**
     * Indicates whether the tooltip is in dark mode.
     */
    isDarkMode?: boolean;
};

const tooltipTheme = getScopedTheme('LuisTooltip');

/**
 * Renders a common tooltip to be used across
 * the website. It differs from the default tooltip
 * that it is dark instead of white.
 *
 * @param props The input properties for the tooltip.
 */
export const LuisTooltip = (props: LuisTooltipProps) => {
    const { isDarkMode = true, ...rest } = props;

    const calloutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
        beak: { backgroundColor: isDarkMode ? tooltipTheme.darkBackgroundColor : tooltipTheme.lightBackgroundColor },
        beakCurtain: { backgroundColor: isDarkMode ? tooltipTheme.darkBackgroundColor : tooltipTheme.lightBackgroundColor },
        calloutMain: {
            backgroundColor: isDarkMode ? tooltipTheme.darkBackgroundColor : tooltipTheme.lightBackgroundColor,
            selectors: {
                '> *': {
                    color: isDarkMode ? tooltipTheme.lightTextColor : tooltipTheme.darkTextColor,
                    lineHeight: '1.5'
                }
            }
        }
    });

    return <TooltipHost {...rest} setAriaDescribedBy calloutProps={{ ...props.calloutProps, styles: calloutStyles }} />;
};
